import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { Observable, of, switchMap, timer } from 'rxjs';

const NATIONAL_IDENTIFICATION_NUMBER_PATTERNS = {
  /* eslint-disable @typescript-eslint/naming-convention */
  NO: /^\d{11}$/g,
  DK: /^\d{6}-\d{4}$/g,
  SE: /^\d{6}-\d{4}$/g,
  FI: /^\d{6}[-+]\d{3}\D{1}$/g,
  /* eslint-enable */
} as const;

// Opposite of the code above, returns invalid chars. (Does not check for length)
// Function is useful if you want to print out invalid characters.
export const NATIONAL_IDENTIFICATION_NUMBER_INVALID_CHARS = {
  /* eslint-disable @typescript-eslint/naming-convention */
  NO: /[^\d]/g,
  DK: /[^\d-]/g,
  SE: /[^\d-]/g,
  FI: /[^\d-+\D]/g,
  /* eslint-enable */
} as const;

export type NationalIdentificationSchema = keyof typeof NATIONAL_IDENTIFICATION_NUMBER_PATTERNS;

function isValidNationalIdentificationSchema(item: unknown): item is NationalIdentificationSchema {
  return Object.keys(NATIONAL_IDENTIFICATION_NUMBER_PATTERNS).includes(item as string);
}

export const NATIONAL_IDENTIFICATION_NUMBER_COMMON_ERRORS = {
  nationalIdentificationNumber: extractTranslation('forms.validation_errors.national_identification_number'),
  nationalIdentificationNumberRequired: extractTranslation(
    'forms.validation_errors.national_identification_number_required',
  ),
  nationalIdentificationSchema: extractTranslation('forms.validation_errors.national_identification_schema'),
  nationalIdentificationNumberNorwayBirthDate: extractTranslation(
    'forms.validation_errors.national_identification_number_norway_birth_date',
  ),
} as const;

type NationalIdentificationCommonError = keyof typeof NATIONAL_IDENTIFICATION_NUMBER_COMMON_ERRORS;

export const NATIONAL_IDENTIFICATION_NUMBER_ERRORS: Record<
  NationalIdentificationSchema,
  Partial<Record<NationalIdentificationCommonError, string>>
> = {
  /* eslint-disable @typescript-eslint/naming-convention */
  NO: {
    nationalIdentificationNumber: extractTranslation('forms.validation_errors.no.national_identification_number'),
  },
  DK: {
    nationalIdentificationNumber: extractTranslation('forms.validation_errors.no.national_identification_number'),
  },
  SE: {
    nationalIdentificationNumber: extractTranslation('forms.validation_errors.no.national_identification_number'),
  },
  FI: {
    nationalIdentificationNumber: extractTranslation('forms.validation_errors.no.national_identification_number'),
  },
  /* eslint-enable */
} as const;

type NationalIdentificationErrors = Partial<Record<NationalIdentificationCommonError, boolean>> | null;

export function isValidNationalIdentificationNumber(
  nationalIdentificationNumber: string,
  schema: NationalIdentificationSchema,
): boolean {
  const pattern = NATIONAL_IDENTIFICATION_NUMBER_PATTERNS[schema] as RegExp | undefined;
  if (pattern) {
    return !!nationalIdentificationNumber.match(pattern);
  }
  return false;
}

function findInvalidNinChars(inputString: string, schema: NationalIdentificationSchema): string | null {
  // Any characters other than a-z, 0-9, dash (-), space ( ), dot (.)
  const schemaRegex = NATIONAL_IDENTIFICATION_NUMBER_INVALID_CHARS[schema];
  if (schemaRegex) {
    const match = inputString.match(schemaRegex);
    return match ? match[0] : null;
  } else {
    console.error('Tried to do regex for national identification number, but schema was not valid');
  }
  return null;
}

// TODO: unit tests
export class NationalIdentificationValidators {
  /**
   * @param nationalIdentificationSchemaControl - a form can contain national identification schema control. In this case it can be provided to the validation function so that we can run validation based on a certain schema, because those are different depending on a country.
   */
  public static numberAndSchemaValidator = (
    nationalIdentificationSchemaControl: FormControl<string | null> | null = null,
  ): ValidatorFn => {
    return function (
      nationalIdentificationNumberControl: AbstractControl<string | null>,
    ): NationalIdentificationErrors | null {
      const nationalIdentificationNumber = nationalIdentificationNumberControl.value?.trim() || '';
      if (!nationalIdentificationNumber) {
        // No national identification number is a valid value.
        // If the national identification number must be required on should add also
        // Validators.required to the list of validation functions,
        return null;
      }
      if (!nationalIdentificationSchemaControl) {
        // If no national identification schema control is provided
        // validation checks if value matches any of national identification schemas.
        const patterns = Object.values(NATIONAL_IDENTIFICATION_NUMBER_PATTERNS);
        const match = patterns.some((pattern) => pattern.test(nationalIdentificationNumber));
        if (!match) {
          return {
            nationalIdentificationNumber: true,
          };
        }
      }
      // Here it checks that the provided national identification schema is valid
      const nationalIdentificationSchemaControlValue = nationalIdentificationSchemaControl?.value;
      const schema = isValidNationalIdentificationSchema(nationalIdentificationSchemaControlValue)
        ? nationalIdentificationSchemaControlValue
        : null;
      if (!schema) {
        return {
          nationalIdentificationSchema: true,
        };
      }
      const pattern = NATIONAL_IDENTIFICATION_NUMBER_PATTERNS[schema] as RegExp | undefined;
      // Here it checks that the provided national identification number corresponds selected schema
      if (pattern) {
        return isValidNationalIdentificationNumber(nationalIdentificationNumber, schema)
          ? null
          : {
              nationalIdentificationNumber: true,
            };
      }
      return null;
    };
  };

  public static numberValidator = (schema: NationalIdentificationSchema = 'NO', delay = 1500): AsyncValidatorFn => {
    return (ctrl: AbstractControl): Observable<NationalIdentificationErrors | null> => {
      const nationalIdentificationNumber = ctrl.value?.trim() || '';
      return of(nationalIdentificationNumber).pipe(
        switchMap((value) => {
          // No national identification number is a valid value.
          // If the national identification number must be required on should add also
          // Validators.required to the list of validation functions,
          if (!value || isValidNationalIdentificationNumber(value, schema)) {
            return of(null);
          } else {
            return timer(delay).pipe(
              switchMap(() =>
                of({
                  nationalIdentificationNumber: true,
                }),
              ),
            );
          }
        }),
      );
    };
  };

  public static ninInvalidCharactersValidator = (schema: NationalIdentificationSchema): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      const invalidChar = findInvalidNinChars(control.value, schema);
      return invalidChar ? { invalidCharacters: { value: invalidChar } } : null;
    };
  };
}
